// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useMemo } from 'react';
import { TaskInput, UserOutput, SearchFormInterface, QuerySelector, TaskOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { TaskForm, TaskValuesForm } from '@Organisms/Task/TaskManage/TaskForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { DEFAULT_GROUP } from '@Constants/task';
import useListQuery from '@Hooks/useListQuery';
import { useParams } from 'react-router-dom';
import { useListAllFields } from '@Queries/describeQueries';
import client from '@Client';
import { computeListSelector } from '@Organisms/User/selector';
import { computeListSelector as tasksComputeListSelector } from '../selector';
import { useCurrentUser } from '@Queries/userQueries';

interface TaskManageProps {
    defaultValues?: TaskInput;
    triggerElem?: ReactElement;
    onFinish: (task: TaskInput) => Promise<any>;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    needDueDate?: boolean;
}

const computeSelectorUserCase = (caseId: string, initialFilters: SearchFormInterface[] = []): QuerySelector => {
    return {
        head: [{ name: 'getCase', idOrName: caseId }, { name: 'assignableUsers' }],
        name: `get-case-${caseId}-assignable-users`,
        initialFilters,
        sort: ['+name'],
    };
};

export const TaskManage = ({
    defaultValues,
    triggerElem,
    onFinish,
    visible,
    onVisibleChange,
    needDueDate = true,
}: TaskManageProps): ReactElement => {
    const i = useLocalMessage();

    const { caseId } = useParams<{ caseId: string }>();
    const { orgId } = useParams<{ orgId: string }>();
    const [loading, setLoading] = React.useState(false);

    const { data: currentUser } = useCurrentUser();
    const organisation = currentUser?.organisation || '';

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const { data: describeData } = useListAllFields();

    const initialFilters = useMemo(
        () => [
            {
                field: 'locked',
                operator: 'any',
                values: [false],
                type: 'boolean',
                describeAttribute: describeData?.user.attributes.find(({ name }) => name === 'locked') as any,
            },
        ],
        [describeData],
    );

    const { data: assignableUsers = [], refetch: refetchAssignableUsers } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: caseId
                ? computeSelectorUserCase(caseId, initialFilters)
                : computeListSelector({ orgId: orgId || organisation }),
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: false,
    });

    const { data: allTaskList = [], refetch: refetchTaskList } = useListQuery<TaskOutput[]>({
        controller: client.TaskController,
        queryParams: {
            selector: tasksComputeListSelector({ caseId, describeData }),
        },
        manual: true,
        fetchOnStart: !!caseId,
    });

    const onFinishHandler = useCallback(
        ({
            group = DEFAULT_GROUP,
            title = '',
            description = '',
            assignee,
            dueDate,
            flag,
            mandatory = false,
        }: TaskValuesForm): Promise<any> => {
            return onFinish({
                group,
                title: title.trim(),
                description: description.trim(),
                assignee,
                dueDate: dueDate?.valueOf(),
                flag,
                mandatory,
            });
        },
        [onFinish],
    );

    return (
        <THDrawer
            title={defaultValues ? i('task.edit.drawer.title') : i('task.create.drawer.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            visible={visible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                setLoading(false);
                if (visible && caseId) {
                    refetchTaskList();
                    refetchAssignableUsers();
                }
                onVisibleChange?.(visible);
            }}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <TaskForm
                    loading={loading}
                    needDueDate={needDueDate}
                    assignableUsers={assignableUsers}
                    allTaskList={allTaskList}
                    onFinish={({ another, ...values }) => {
                        setLoading(true);
                        onFinishHandler(values).then(() => {
                            if (!another) {
                                toggleDrawer();
                                return;
                            }

                            form.resetFields();
                            refetchAssignableUsers();
                            refetchTaskList();
                            onFormValuesChange();
                            setFieldsTouched(false);
                            setLoading(false);
                        });
                    }}
                    form={form}
                    defaultValues={defaultValues}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    isValid={isValid && fieldsTouched}
                    onCancel={() => {
                        toggleDrawer();
                        form.resetFields();
                        setFieldsTouched(false);
                    }}
                />
            )}
        </THDrawer>
    );
};
